// CognitoAuthService — wrapper around amazon-cognito-identity-js used by the
// backoffice for SRP login, session refresh, password reset, MFA, and logout.
//
// Configured via:
//   VITE_COGNITO_USER_POOL_ID  e.g. "eu-west-3_XXXXXXXXX"
//   VITE_COGNITO_CLIENT_ID     e.g. the BackofficeClient id from the
//                               betterfield-cognito-test stack output
//
// These come from the deploy-time .env (the backoffice CI pipeline pulls
// the .env from S3 and Vite bakes VITE_* into the build at build time).

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

// VITE_* values are read once at module load: Vite bakes them into the
// build, and within Vitest they snapshot at the first import. Tests that
// exercise different "configured / not configured" scenarios must set
// import.meta.env BEFORE importing this module, or use vi.isolateModules /
// __unstable.resetPool to control state between cases.
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;

let cachedPool = null;

function getUserPool() {
  if (!cachedPool) {
    if (!USER_POOL_ID || !CLIENT_ID) {
      throw new Error(
        'Cognito is not configured: set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID',
      );
    }
    cachedPool = new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID });
  }
  return cachedPool;
}

export function isCognitoConfigured() {
  return Boolean(USER_POOL_ID && CLIENT_ID);
}

// Authenticate via USER_PASSWORD_AUTH. Resolves with a status discriminator so the
// caller can branch on success vs challenge:
//   - { status: 'SUCCESS', user, session }
//   - { status: 'NEW_PASSWORD_REQUIRED', user, userAttributes }
//   - { status: 'TOTP_REQUIRED', user, challengeName, challengeParams }
//   - { status: 'MFA_REQUIRED', user, challengeName, challengeParams }
// Rejects with the underlying CognitoError on real auth failures.
//
// The CognitoUser instance is returned alongside the challenge so the caller
// can pass it to completeNewPasswordChallenge / sendMFACode etc.
export function login(email, password) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getUserPool() });
    // USER_PASSWORD_AUTH is required for the migrate-user Lambda to fire on
    // first login of a legacy user (Cognito only invokes the trigger when
    // the password actually crosses the wire — SRP keeps it client-side).
    // After migration the same flow keeps working, so we use it
    // consistently rather than feature-detecting per user.
    user.setAuthenticationFlowType('USER_PASSWORD_AUTH');
    const auth = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(auth, {
      onSuccess: (session) => resolve({ status: 'SUCCESS', user, session }),
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        // Cognito rejects passing back immutable attributes during the
        // password-change challenge. Strip them so the caller can forward
        // userAttributes as-is to completeNewPasswordChallenge.
        delete userAttributes.email_verified;
        delete userAttributes.email;
        resolve({ status: 'NEW_PASSWORD_REQUIRED', user, userAttributes });
      },
      mfaRequired: (challengeName, challengeParams) => {
        resolve({ status: 'MFA_REQUIRED', user, challengeName, challengeParams });
      },
      totpRequired: (challengeName, challengeParams) => {
        resolve({ status: 'TOTP_REQUIRED', user, challengeName, challengeParams });
      },
    });
  });
}

// Complete the NEW_PASSWORD_REQUIRED challenge. `user` is the CognitoUser
// returned by login().
export function completeNewPasswordChallenge(user, newPassword, attributes = {}) {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, attributes, {
      onSuccess: (session) => resolve({ status: 'SUCCESS', user, session }),
      onFailure: (err) => reject(err),
      // Pool-enforced MFA can fire after a successful password change.
      // Resolve so the caller can branch rather than leaving the promise pending.
      mfaRequired: (challengeName, challengeParams) =>
        resolve({ status: 'MFA_REQUIRED', user, challengeName, challengeParams }),
      totpRequired: (challengeName, challengeParams) =>
        resolve({ status: 'TOTP_REQUIRED', user, challengeName, challengeParams }),
    });
  });
}

// Sign out the current user. globalSignOut revokes the refresh token
// server-side (and clears the local cache on success), which matters because
// access tokens are short-lived but refresh tokens last ~30 days. If the
// server call fails (e.g. expired access token, offline), fall back to a
// local signOut so the UI still ends up in a logged-out state.
export function logout() {
  return new Promise((resolve) => {
    const user = getUserPool().getCurrentUser();
    if (!user) {
      resolve();
      return;
    }
    user.globalSignOut({
      onSuccess: () => resolve(),
      onFailure: () => {
        user.signOut();
        resolve();
      },
    });
  });
}

// Return the active CognitoUserSession, refreshing it via the SDK if expired.
// Resolves to null when there's no signed-in user. Rejects when the SDK can't
// recover the session (e.g. revoked refresh token).
export function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const user = getUserPool().getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }
    user.getSession((err, session) => {
      if (err) reject(err);
      else resolve(session);
    });
  });
}

// Same as getCurrentSession — exposed under a more explicit name for callers
// that want to make the refresh intent obvious. The SDK refreshes
// transparently inside getSession when the access/id tokens are expired.
export function refreshSession() {
  return getCurrentSession();
}

// Step 1 of the forgot-password flow. Asks Cognito to send a 6-digit
// verification code to the user's verified email. With
// PreventUserExistenceErrors enabled on the pool, Cognito returns a
// generic CodeDeliveryDetails for unknown emails too, so the caller
// should not branch UI on whether the user actually exists.
export function forgotPassword(email) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getUserPool() });
    user.forgotPassword({
      onSuccess: (data) => resolve(data),
      onFailure: (err) => reject(err),
      // Fired by the SDK as soon as the code is dispatched. We treat this as
      // success for step 1 — the user now sees the "enter code" UI.
      inputVerificationCode: (data) => resolve(data),
    });
  });
}

// Step 2: submit the code + new password. Stateless (no need to reuse the
// CognitoUser from step 1); we recreate a CognitoUser by email.
export function confirmPassword(email, code, newPassword) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getUserPool() });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

// Begin TOTP setup for the current signed-in user. Calls
// AssociateSoftwareToken on Cognito and returns the shared secret + the
// signed-in username so the caller can build the otpauth:// URI for the
// QR code without re-querying the SDK. Cognito does not provide recovery
// codes natively — that's a deliberate gap vs the legacy bcrypt flow.
export function setupTOTP() {
  return new Promise((resolve, reject) => {
    const user = getUserPool().getCurrentUser();
    if (!user) {
      reject(new Error('No active user'));
      return;
    }
    user.getSession((sessionErr) => {
      if (sessionErr) {
        reject(sessionErr);
        return;
      }
      user.associateSoftwareToken({
        associateSecretCode: (secretCode) => resolve({
          secretCode,
          username: user.getUsername(),
        }),
        onFailure: (err) => reject(err),
      });
    });
  });
}

// Verify the TOTP code from the authenticator app and enable
// SOFTWARE_TOKEN_MFA as the user's preferred MFA. After this call the next
// SRP login will trigger a TOTP_REQUIRED challenge.
export function verifyTOTP(code, deviceName = 'Authenticator') {
  return new Promise((resolve, reject) => {
    const user = getUserPool().getCurrentUser();
    if (!user) {
      reject(new Error('No active user'));
      return;
    }
    user.getSession((sessionErr) => {
      if (sessionErr) {
        reject(sessionErr);
        return;
      }
      user.verifySoftwareToken(code, deviceName, {
        onSuccess: () => {
          user.setUserMfaPreference(
            null,
            { PreferredMfa: true, Enabled: true },
            (prefErr) => {
              if (prefErr) reject(prefErr);
              else resolve({ status: 'VERIFIED' });
            },
          );
        },
        onFailure: (err) => reject(err),
      });
    });
  });
}

// Disable SOFTWARE_TOKEN_MFA for the current user. setUserMfaPreference
// flips the flag on the user pool — next login will not challenge for a
// TOTP code. The Cognito secret stays associated with the user, so re-
// enabling later does not require a fresh setup.
export function disableTOTP() {
  return new Promise((resolve, reject) => {
    const user = getUserPool().getCurrentUser();
    if (!user) {
      reject(new Error('No active user'));
      return;
    }
    user.getSession((sessionErr) => {
      if (sessionErr) {
        reject(sessionErr);
        return;
      }
      user.setUserMfaPreference(
        null,
        { PreferredMfa: false, Enabled: false },
        (err) => {
          if (err) reject(err);
          else resolve();
        },
      );
    });
  });
}

// Read the current user's MFA configuration from Cognito. Returns whether
// SOFTWARE_TOKEN_MFA is enabled. Resolves to { enabled: false } when there
// is no signed-in user (the caller treats this as "not configured").
export function getTOTPStatus() {
  return new Promise((resolve, reject) => {
    const user = getUserPool().getCurrentUser();
    if (!user) {
      resolve({ enabled: false });
      return;
    }
    user.getSession((sessionErr) => {
      if (sessionErr) {
        reject(sessionErr);
        return;
      }
      user.getUserData(
        (err, data) => {
          if (err) {
            reject(err);
            return;
          }
          const list = (data && data.UserMFASettingList) || [];
          resolve({
            enabled: list.includes('SOFTWARE_TOKEN_MFA'),
            preferred: data && data.PreferredMfaSetting === 'SOFTWARE_TOKEN_MFA',
          });
        },
        // Bypass the SDK's local cache so we always see the freshly-flipped
        // preference after a setup / disable round-trip.
        { bypassCache: true },
      );
    });
  });
}

// Respond to an MFA challenge with the 6-digit code from the authenticator
// app. `user` is the CognitoUser instance returned by login(); `mfaType`
// must match the challenge name ('SOFTWARE_TOKEN_MFA' or 'SMS_MFA').
export function sendMFACode(user, code, mfaType = 'SOFTWARE_TOKEN_MFA') {
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve({ status: 'SUCCESS', user, session }),
        onFailure: (err) => reject(err),
      },
      mfaType,
    );
  });
}

// Exposed for tests / debug only — do not call directly from app code.
export const __unstable = { getUserPool, resetPool: () => { cachedPool = null; } };
